import _ from 'lodash';
import moment from 'moment';

import cashflowViewConsts from '../../constants/cashflow-view';

const HIDDEN_EXCLUDED = {
  LOGIC: 'logic',
};

const SHORT_LABEL = {
  'תחבורה ציבורית': 'תחב"צ',
};

const FORMATTED_LABEL = {
  כלכלה: 'סופר',
};

function normalizeCashflow(cashflow) {
  return {
    [cashflowViewConsts.cashflowTypes.income]: _getIncomeSubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.variableExpense]: _getVariableExpenseSubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.fixedExpense]: _getFixedExpenseSubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.trackingCategory]: _getTrackingCategoriesSubtree(cashflow),
    [cashflowViewConsts.cashflowTypes.excluded]: _getExcludedTransactions(cashflow),
  };
}

function _getIncomeSubtree(cashflow) {
  const { fixedTree } = cashflow;
  return {
    upToNowAmount: cashflow.incomeEnvelopesActualSum + cashflow.variableIncomeSum,
    recommendedOrExpectedAmount: cashflow.incomeEnvelopesBalanceSum + cashflow.variableIncomeBalancedAmount,
    fixedIncome: {
      categories: _.map(fixedTree.incomeCategories, _getFixCategory),
    },
    variableIncome: {
      categories: [_getVariableDetails(cashflow.variableIncomeSum, cashflow.variableIncomeBalancedAmount, cashflow.variableIncomeTransactions)],
    },
  };
}

function _getExcludedTransactions(cashflow) {
  const allExcluded = visibleExcluded(cashflow.excluded);
  const [income, expense] = _.chain(allExcluded).sortBy(t => t.transactionDate).partition(t => t.isIncome).value();
  return {
    excludedIncome: {
      categories: [{
        label: 'הכנסות',
        actualsSum: _.sumBy(income, t => t.incomeAmount),
        transactions: income,
      }],
    },
    excludedExpense: {
      categories: [{
        label: 'הוצאות',
        actualsSum: _.sumBy(expense, t => t.billingAmount),
        transactions: expense,
      }],
    },
  };
}

function visibleExcluded(transactions) {
  return _.reject(transactions, t => t.excludeReason === HIDDEN_EXCLUDED.LOGIC);
}

function _getTrackingCategoriesSubtree(cashflow) {
  const [weeklyTracking, monthlyTracking] = _.partition(cashflow.getEnvelopesByType(cashflowViewConsts.cashflowTypes.trackingCategory),
    e => e.details.trackingCategory.frequency === 'weekly');
  return [..._parseWeeklyTrackingCategory(weeklyTracking), ..._parseMonthlyTrackingCategory(monthlyTracking)];
}

function _parseMonthlyTrackingCategory(monthlyTracking) {
  return _.map(monthlyTracking, envelope => {
    return {
      envelopeId: envelope.id,
      categoryName: FORMATTED_LABEL[envelope.details.trackingCategory.name] || envelope.details.trackingCategory.name,
      categoryId: envelope.details.trackingCategory._id,
      upToNowAmount: envelope.actualsAmount,
      recommendedOrExpectedAmount: envelope.predictedAmount,
      categories: [_trackingCategoryEnvelopeToCategory(envelope)],
      icon: envelope.details.trackingCategory.icon,
      frequency: 'monthly',
    };
  });
}

function _parseWeeklyTrackingCategory(weeklyTracking) {
  const weeklyByType = _.groupBy(weeklyTracking, e => e.details.trackingCategory.name);
  return _.map(weeklyByType, envelopes => {
    const categoryName = FORMATTED_LABEL[envelopes[0].details.trackingCategory.name] || envelopes[0].details.trackingCategory.name;
    return {
      categoryName,
      categoryId: envelopes[0].details.trackingCategory._id,
      upToNowAmount: _.sumBy(envelopes, envelope => envelope.actualsAmount),
      recommendedOrExpectedAmount: _.sumBy(envelopes, envelope => envelope.balancedAmount),
      icon: envelopes[0].details.trackingCategory.icon,
      frequency: 'weekly',
      categories: _.chain(envelopes)
        .sortBy(envelope => envelope.details.weeklyIndex)
        .map(e => _weeklyEnvelopeToCategory(e, categoryName))
        .value(),
    };
  });
}

function _getFixedExpenseSubtree(cashflow) {
  const { fixedTree } = cashflow;
  return {
    upToNowAmount: fixedTree.fixedExpenseEnvelopesActualSum,
    recommendedOrExpectedAmount: fixedTree.fixedExpenseEnvelopesBalanceSum,
    categories: _getOrderedFixedCategories(fixedTree.expenseCategories),
  };
}

function _getOrderedFixedCategories(expenseCategories) {
  const orderedExpenses = _getOrderedExpenseNames(_.keys(expenseCategories));
  return _.chain(expenseCategories)
    .map(_getFixCategory)
    .sortBy(_.partialRight(_getFixedOrderIndex, orderedExpenses))
    .value();
}

function _getOrderedExpenseNames(expenseNames) {
  const knownPositionExpenses = _.intersection([
    cashflowViewConsts.OTHER_CATEGORY,
    ..._.sortBy(cashflowViewConsts.PAYMENT_METHODS_FIXED_EXPENSE_CATEGORIES),
  ], expenseNames);
  const regularExpenses = _.chain(expenseNames)
    .difference(knownPositionExpenses)
    .sortBy()
    .value();
  return [...regularExpenses, ...knownPositionExpenses];
}

function _getVariableExpenseSubtree(cashflow) {
  const currentMonthVariableExpenses = cashflow.weeklyVariables.toJson();
  return {
    upToNowAmount: cashflow.variableActualSum,
    recommendedOrExpectedAmount: _.max([cashflow.variableBalancedAmount, 0]),
    monthly: {
      realExpenses: {
        categories: [_getVariableDetails(
          cashflow.variableActualSum,
          cashflow.variableBalancedAmount,
          cashflow.variableTransactions,
        )],
      },
    },
    weekly: {
      realExpenses: {
        categories: _.map(currentMonthVariableExpenses.weeks, week => {
          const label = `שבוע ${week.weekIndex + 1}`;
          const { sum: actualsSum, transactions, remaining: recommendedAmount } = week;
          const weekTimeFrame = _getEnvelopeWeekTimeFrame(week, moment());
          const disabled = _isFutureWeek(weekTimeFrame) && week.transactions.length === 0;
          const sortedTransactions = _.sortBy(transactions, t => t.transactionDate);
          return {
            ...week,
            label,
            actualsSum,
            transactions: sortedTransactions,
            recommendedAmount,
            disabled,
            initiallyExpanded: false,
            timeFrameDetails: {
              timeFrame: cashflowViewConsts.envelopeTimeFrames.weekly,
              isCurrentWeek: weekTimeFrame === cashflowViewConsts.weekTimeFrames.current,
              weeklyIndex: week.weekIndex,
            },
          };
        }),
      },
    },
  };
}

function _isFutureWeek(weekTimeFrame) {
  return weekTimeFrame === cashflowViewConsts.weekTimeFrames.future;
}

function _getFixCategory(fixCategory) {
  return {
    label: fixCategory.expense,
    actualsSum: fixCategory.actualsAmount,
    recommendedAmount: fixCategory.balancedAmount,
    transactions: toPredictedTransactions(fixCategory.envelopes),
    initiallyExpanded: false,
  };
}

function _getFixedOrderIndex(fixCategory, allExpenseCategoriesOrdered) {
  return _.findIndex(allExpenseCategoriesOrdered, c => c === fixCategory.label);
}

function _weeklyEnvelopeToCategory(envelope, categoryName) {
  const { weeklyIndex } = envelope.details;
  const label = `שבוע ${weeklyIndex + 1}`;
  const weekTimeFrame = _getEnvelopeWeekTimeFrame(envelope.details, moment());
  return {
    label,
    parentCategory: categoryName,
    actualsSum: envelope.actualsAmount,
    recommendedAmount: envelope.balancedAmount,
    transactions: _.sortBy(envelope.actuals, t => t.transactionDate),
    initiallyExpanded: false,
    disabled: weekTimeFrame === cashflowViewConsts.weekTimeFrames.future && _.isEmpty(envelope.actuals),
    isEditable: !envelope.balanceDatePassed,
    categoryId: envelope.details.trackingCategory._id,
    envelopeId: envelope.id,
    timeFrameDetails: {
      timeFrame: cashflowViewConsts.envelopeTimeFrames.weekly,
      weeklyIndex,
      startDate: envelope.details.startDate,
      endDate: moment(envelope.details.endDate).subtract(1, 'days').format('YYYY-MM-DD'),
      isCurrentWeek: weekTimeFrame === cashflowViewConsts.weekTimeFrames.current,
    },
  };
}

function _trackingCategoryEnvelopeToCategory(envelope) {
  const { trackingCategory } = envelope.details;
  return {
    label: trackingCategory.name,
    shortLabel: SHORT_LABEL[trackingCategory.name],
    parentCategory: trackingCategory.name,
    actualsSum: envelope.actualsAmount,
    recommendedAmount: envelope.balancedAmount,
    transactions: _.sortBy(envelope.actuals, t => t.transactionDate),
    initiallyExpanded: true,
    disabled: false,
    isEditable: !envelope.balanceDatePassed,
    categoryId: envelope.details.trackingCategory._id,
    envelopeId: envelope.id,
    timeFrameDetails: {
      timeFrame: cashflowViewConsts.envelopeTimeFrames.monthly,
    },
  };
}

function _getVariableDetails(actualsSum, recommendedAmount, transactions) {
  return {
    label: 'משתנות',
    actualsSum,
    recommendedAmount,
    transactions: _.sortBy(transactions, t => t.transactionDate),
    initiallyExpanded: true,
    disabled: false,
  };
}

function toPredictedTransactions(envelopes) {
  return _.map(envelopes, envelope => {
    return {
      name: envelope.details.businessName,
      expense: envelope.details.expense,
      predictedAmount: envelope.balancedAmount,
      actual: envelope.actuals[0],
      previousTransactionId: envelope.details.previousTransactionId,
      predictedTransactionDate: envelope.details.transactionDate,
      balanceDatePassed: envelope.balanceDatePassed,
      envelopeId: envelope.id,
      sequenceId: envelope.details.sequenceId,
      isIncome: _isIncome(envelope.originalAmount),
    };
  });
}

function _isIncome(amount) {
  return amount < 0;
}

function _getEnvelopeWeekTimeFrame(weekDetails, now) {
  const { endDate, startDate } = weekDetails;
  if (moment(endDate) <= now) {
    return cashflowViewConsts.weekTimeFrames.past;
  }
  if (moment(startDate) <= now && now < moment(endDate)) {
    return cashflowViewConsts.weekTimeFrames.current;
  }
  return cashflowViewConsts.weekTimeFrames.future;
}

export default {
  normalizeCashflow,
  visibleExcluded,
};
